import React, { useEffect, useState, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Alert, Col, Radio, Row, Space } from 'antd'
import {
  formatAddress,
  formatDateTime,
  getPurchaseStatus,
  numberWithComma,
  roundNumber,
} from 'helpers/format'
import { PickUpProduct, PurchaseDetailReq } from 'Store/purchase/types'
import { getPurchaseDetailAPI } from 'Store/purchase/api'
import { selectPurchaseDetail, selectPurchaseDetailEvent } from 'Store/purchase/selector'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/grn/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import ProductsTable from './components/ProductsTable'
import { selectAllSupplierList } from 'Store/supplier/selector'
import { InfoItem } from 'Pages/Merchants/components'
import { AppTutorial, StatusTag } from 'Components/utilities'
import { getUserLogin } from 'helpers/account'
import PurchasePDF from 'Pages/Purchase/components/PurchasePDF'
import ProductsPickUpTable from './components/ProductsPickUpTable'
import PickUpForm from './components/PickUpForm'
import { cloneDeep } from 'lodash'
import { CreateGrnReq, GrnItem } from 'Store/grn/types'
import { createGrnAPI } from 'Store/grn/api'
import { selectGrnActionEvent } from 'Store/grn/selector'
import ConfirmWithChild from 'Components/modal/ConfirmWithChild'
import { AppInput } from 'Components/form'
import { PrimaryButton } from 'Components/button'
import { NumberOutlined } from '@ant-design/icons'
import { TutorialPurchasePage } from 'helpers/tutorials'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  id?: number
}

const PickupDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    detail,
    detailEvent,
  } = props

  const dispatch = useDispatch()
  const [selectedProduct, setSelectedProduct] = useState(undefined as PickUpProduct | undefined)
  const [productsPickup, setProductsPickup] = useState([] as PickUpProduct[])
  const [pageMode, setPageMode] = useState('add' as 'add' | 'edit')
  const [defaultCostModal, setDefaultCostModal] = useState(false)
  const [defaultCost, setDefaultCost] = useState(0)
  const [costType, setCostType] = useState('avg' as 'avg' | 'sku')
  const [showConfirm, setShowConfirm] = useState(false)
  const [drawer, setDrawer] = useState(false)
  const purchasePDF = useRef(null as any)
  const purchaseLitePDF = useRef(null as any)
  const userInfo = getUserLogin()

  // tutorial
  const [showTutorial, setShowTutorial] = useState(false as boolean)
  const steps = TutorialPurchasePage.stock.steps
  const enableTutorial = TutorialPurchasePage.stock.enable

  useEffect(() => {
    if (visible) {
      onGetDetail()
      setProductsPickup([])
      setSelectedProduct(undefined)
    }
  }, [visible])

  const onGetDetail = (): void => {
    const req: PurchaseDetailReq = {
      id,
    }
    dispatch(getPurchaseDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const formatGrnItem = () => {
    const grnItem = [] as GrnItem[]
    productsPickup.forEach(item => {
      grnItem.push({
        hidden_cost: item.hidden_cost,
        received_amount: item.received_amout,
        purchasing_item_id: item.purchasing_item_id || 0,
        online_percentage: item?.percent || 0,
      })
    })
    return grnItem
  }

  const onConfirmPickup = (supplier_shipping_no: string): void => {
    const req: CreateGrnReq = {
      supplier_id: detail?.supplier_id || 0,
      purchasing_id: detail?.id || 0,
      items: formatGrnItem(),
      username: userInfo?.username || '',
      supplier_shipping_no,
    }
    dispatch(createGrnAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onOpenPickUpDrawer = () => {
    setDrawer(true)
  }

  const onClosePickUpDrawer = () => {
    setDrawer(false)
  }

  const onClickAdd = (product: PickUpProduct) => {
    setPageMode('add')
    setSelectedProduct(product)
    onOpenPickUpDrawer()
  }

  const onUpdateDefaultCost = (val: string) => {
    setDefaultCost(parseInt(val))
  }

  const calculateAvgCost = () => {
    let amount = 0
    productsPickup &&
      productsPickup.forEach(item => {
        amount = item.received_amout + amount
      })
    // return roundNumber(defaultCost / amount || 0)
    return {
      amount: amount,
      avg: roundNumber(defaultCost / amount || 0),
    }
  }

  const renderAmountModal = () => {
    return (
      <Row gutter={[8, 8]} justify="start">
        <Col span={24}>
          <Radio.Group onChange={e => setCostType(e.target.value)} value={costType}>
            <Radio value={'avg'}>ต้นทุนแฝงเฉลี่ย</Radio>
            <Radio value={'sku'}>ต้นทุนแฝงต่อรายการสินค้า (SKU)</Radio>
          </Radio.Group>
        </Col>
        <Col span={24}>
          <AppInput
            name="amount"
            label="ระบุต้นทุนแฝง"
            type="number"
            isRequired={false}
            isError={false}
            value={defaultCost}
            onChange={onUpdateDefaultCost}
            resProps={{ suffix: 'บาท' }}
          />
        </Col>
        {costType === 'avg' && (
          <Col span={24}>
            <Space direction="vertical">
              <Alert
                message={`จำนวนรับเข้า ${numberWithComma(calculateAvgCost().amount)} หน่วย`}
                description={`ต้นทุนแฝงเฉลี่ย ${numberWithComma(calculateAvgCost().avg, true)} บาท /หน่วย`}
                type="info"
              />
              <Remark>
                หมายเหตุ : หากมีการเปลี่ยนแปลงจำนวนการรับเข้าในภายหลัง
                กรุณาระบุต้นทุนแฝงใหม่เพื่อคำนวนหาค่าเฉลี่ยใหม่
              </Remark>
            </Space>
          </Col>
        )}
      </Row>
    )
  }

  const onSaveAmount = () => {
    const hiddenCost = costType === 'avg' ? calculateAvgCost().avg : defaultCost
    const newProduct = productsPickup?.map((item: PickUpProduct) => {
      item.hidden_cost = costType === 'avg' ? hiddenCost * item.received_amout : hiddenCost
      return item
    })
    setProductsPickup([...newProduct])
    setDefaultCostModal(false)
  }

  const DefaultAmountModal = (
    <ConfirmWithChild
      title={'ต้นทุนแฝง'}
      isShow={defaultCostModal}
      handleOk={onSaveAmount}
      handleCancel={() => setDefaultCostModal(false)}
      child={renderAmountModal()}
      cancelBtnTxt="ปิด"
    />
  )

  const onClickAddAllProduct = (product: PickUpProduct[]) => {
    setProductsPickup([...product])
  }

  const onClickEdit = (product: PickUpProduct) => {
    setPageMode('edit')
    setSelectedProduct(product)
    onOpenPickUpDrawer()
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const renderPurchaseInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดการสั่งซื้อ</BoxTitle>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            {renderInfo('เลขที่ใบสั่งซื้อ', detail?.purchasing_no)}
            {renderInfo('วันที่สั่งซื้อ', formatDateTime(detail?.purchasing_date || ''))}
            {renderInfo('บริษัท/Supplier', detail?.supplier?.name)}
            {renderInfo('ที่อยู่', detail?.supplier?.address)}
            {renderInfo('เบอร์โทรศัพท์', detail?.supplier?.mobile_no)}
            {renderInfo('สถานะ', renderStatus(detail?.status || ''))}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderClientInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>ข้อมูลลูกค้า</BoxTitle>
        {renderInfo('ชื่อ', detail?.merchant?.name)}
        {renderInfo(
          'ที่อยู่',
          formatAddress(
            detail?.merchant?.address,
            detail?.merchant?.district,
            detail?.merchant?.amphor,
            detail?.merchant?.province,
            detail?.merchant?.postcode,
          ),
        )}
        {renderInfo('เบอร์โทรศัพท์', detail?.merchant?.phone)}
        {renderInfo('ผู้ออกใบสั่งซื้อ', detail?.purchasing_by)}
        {renderInfo('หมายเหตุ', detail?.remark)}
      </BoxWrapper>
    )
  }

  const isSelected = (purchasing_id: number) => {
    const data = productsPickup.find(item => (item.purchasing_item_id || 0) === purchasing_id)
    return data ? true : false
  }

  const onSaveProductPickup = (product: PickUpProduct) => {
    let selected = cloneDeep(productsPickup) as PickUpProduct[]
    if (isSelected(product?.purchasing_item_id || 0)) {
      selected = selected.map(item => {
        if (item.purchasing_item_id === (product?.purchasing_item_id || 0)) {
          return product
        } else {
          return item
        }
      })
    } else {
      selected.push(product)
    }
    setProductsPickup([...selected])
  }

  const PickUpFormDrawer = (
    <PickUpForm
      mode={pageMode}
      product={selectedProduct}
      visible={drawer}
      onCloseDrawer={onClosePickUpDrawer}
      onSave={onSaveProductPickup}
    />
  )

  const renderStatus = (status: string) => {
    const statusResult = getPurchaseStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const renderFooter = () => {
    return (
      <Col span={24}>
        <Row justify="start" gutter={[16, 16]}>
          <Col>
            <PrimaryButton btnType="Cancel" text={`ปิด`} onClick={onCloseDrawer} />
          </Col>
          <Col>
            <PrimaryButton
              btnType="Primary"
              text={`ยืนยันรับเข้าคลัง (${productsPickup?.length} รายการ)`}
              onClick={() => setShowConfirm(true)}
              disabled={productsPickup?.length <= 0}
              className="ttr-save"
            />
          </Col>
        </Row>
      </Col>
    )
  }

  return (
    <DetailDrawer
      title={'รายการรับสินค้าเข้าคลัง'}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      width={'90%'}
      maskClosable={false}
      footer={renderFooter()}
      isShowTutorial={showTutorial}
      showTutorial={enableTutorial}
      onClickStart={() => setShowTutorial(true)}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      {PickUpFormDrawer}
      {DefaultAmountModal}
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={[16, 16]}>
        <Col md={12} xs={24} className="ttr-info-sup">
          {renderPurchaseInfo()}
        </Col>
        <Col md={12} xs={24} className="ttr-info-merchant">
          {renderClientInfo()}
        </Col>
        <Col md={24} xs={24} className="ttr-product">
          <ProductsTable
            products={detail?.purchasing_item || []}
            productsPickup={productsPickup}
            purchasePdfRef={purchasePDF}
            purchaseLitePdfRef={purchaseLitePDF}
            purchasing_no={detail?.purchasing_no}
            onClickPickUp={onClickAdd}
            onClickPickUpAll={onClickAddAllProduct}
          />
        </Col>
        <Col span={12}></Col>
        {productsPickup.length > 0 && (
          <Col>
            <Space>
              <PrimaryButton
                btnType="Approve"
                text="ระบุต้นทุนแฝง"
                icon={<NumberOutlined />}
                onClick={() => {
                  setDefaultCost(0)
                  setCostType('avg')
                  setDefaultCostModal(true)
                }}
                size="middle"
                className="ttr-hidden-cost"
              />
            </Space>
          </Col>
        )}
        <Col className="ttr-product-pickup">
          <ProductsPickUpTable
            products={productsPickup}
            onUpdateData={(product: PickUpProduct[]) => {
              setProductsPickup(product)
            }}
            onClickEdit={(product: PickUpProduct) => {
              onClickEdit(product)
            }}
            onClickConfirmPickup={onConfirmPickup}
            showConfirm={showConfirm}
            setShowConfirm={setShowConfirm}
          />
        </Col>
      </Row>
      <Row ref={purchasePDF}>
        <Col span={24}>
          <PurchasePDF detail={detail} />
        </Col>
      </Row>
      <Row ref={purchaseLitePDF}>
        <Col span={24}>
          <PurchasePDF detail={detail} isLite />
        </Col>
      </Row>
      {enableTutorial && (
        <AppTutorial
          onCloaseTutorial={() => setShowTutorial(false)}
          showTutorial={showTutorial}
          steps={steps}
        />
      )}
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const Remark = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  text-align: left;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectGrnActionEvent(state),
    detail: selectPurchaseDetail(state),
    detailEvent: selectPurchaseDetailEvent(state),
    supplierList: selectAllSupplierList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(PickupDetail)
